import { makeAutoObservable } from 'mobx'
import type { IIconProps } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISmartCallFlowCallQueueStep } from 'entities/SmartCall/api/types'
import type { Inbox } from 'entities/Inbox/model/Inbox'
import { FlowActionStore } from 'widgets/Flow'
import {
  generateSmartCallActionId,
  type ISmartCallActionStore,
  type ISmartCallFlowActionStoreProps,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/Action'
import { GreetingAudioBlockStore } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/blocks/GreetingAudioBlock'
import {
  allSmartCallFlowActionTypes,
  type ISmartCallFlowActionType,
} from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'

export type ICallQueueStoreStoreProps = {
  id?: ISmartCallFlowActionStoreProps['id']
  parentId: ISmartCallFlowActionStoreProps['parentId']
  args?: ISmartCallFlowCallQueueStep['args']
}

export class CallQueueStore {
  static readonly type = 'callQueue' satisfies ISmartCallFlowActionType
  static title = 'Call queue'
  static iconProps: IIconProps = {
    icon: 'clock',
    color: 'var(--action-contained-primary-normal)',
  }
  public readonly actionStore: ISmartCallActionStore

  readonly greetingMessageStore: GreetingAudioBlockStore
  readonly greetingMusicStore: GreetingAudioBlockStore

  constructor(
    private _entities: IEntitiesStore,
    { id, args, ...props }: ICallQueueStoreStoreProps,
    private parent: { inbox: Inbox | null }
  ) {
    this.actionStore = new FlowActionStore({
      ...props,
      id: id || generateSmartCallActionId(`${this._stepIdType.type}_${this._stepIdType.id}`),
      type: CallQueueStore.type,
      name: CallQueueStore.title,
      allowedBeforeActions: allSmartCallFlowActionTypes.filter((type) => type !== 'callQueue'),
      allowedNextActions: ['sendMessage', 'sendVoicemail', 'aiTextingAgent', 'aiCallingAgent'],
    })
    this.greetingMessageStore = new GreetingAudioBlockStore(
      this._entities,
      this.parent,
      'Your message is the key – please select an audio',
      args?.greeting_message_audio_url
        ? {
            audio_url: args.greeting_message_audio_url,
          }
        : undefined
    )
    this.greetingMusicStore = new GreetingAudioBlockStore(
      this._entities,
      this.parent,
      'Your message is the key – please select an audio',
      args?.greeting_music_audio_url
        ? {
            audio_url: args.greeting_music_audio_url,
          }
        : undefined
    )
    makeAutoObservable(this)
  }

  private _stepIdType: Pick<ISmartCallFlowCallQueueStep, 'id' | 'type'> = {
    id: CallQueueStore.type,
    type: 'action',
  }

  get step(): ISmartCallFlowCallQueueStep {
    return {
      ...this._stepIdType,
      args: {
        greeting_message_audio_url: this.greetingMessageStore.args.audio_url,
        greeting_music_audio_url: this.greetingMusicStore.args.audio_url,
      },
      next: this.actionStore.nextId,
    }
  }

  setApiError = (key: string, message: string) => {
    if (key.includes('greeting_message_audio_url')) {
      this.greetingMessageStore.setError(message)
    }
    if (key.includes('greeting_music_audio_url')) {
      this.greetingMusicStore.setError(message)
    }
  }

  validate = () => {
    let valid = true
    if (!this.greetingMessageStore.validate()) {
      valid = false
    }
    if (!this.greetingMusicStore.validate()) {
      valid = false
    }
    return valid
  }

  get isConfigurationError() {
    return !!this.greetingMessageStore.error || !!this.greetingMusicStore.error
  }
}
